/**
 * O Painel: o dashboard que abre o sistema e leva a cada área. Pedido do fundador em 19/09/2026:
 * "tem que construir um dashboard (painel) completo que ficará acima dos compromissos (clicável)
 * levando para cada área do sistema." Não existe no admin atual: é funcionalidade nova.
 *
 * O QUE ELE MOSTRA:
 *   1. Um cartão por área que o papel da pessoa vê, com de 1 a 3 números reais daquela área. O
 *      cartão inteiro é um link que abre a área. As áreas, os nomes e os ícones saem do manifesto
 *      (`studiolu-admin.config.ts`), então o Painel nunca diverge do menu.
 *   2. Dois gráficos interativos (regra dura da casa): o funil de leads por etapa e a jornada das
 *      clientes por etapa. Tocar numa barra, ou na etiqueta da etapa abaixo dela, abre a área já
 *      recortada naquela etapa.
 *
 * ===========================================================================
 * OS FILTROS QUE CADA ÁREA DEVE HONRAR, porque as telas das áreas são de outras frentes
 * ===========================================================================
 * O Painel navega com `navegar('<rota>?<parametro>=<valor>')`, e a tela da área lê com
 * `filtroDaRota(rota)` (`rotas.tsx`). Cada área abaixo precisa abrir já recortada:
 *
 *   /vendas?etapa=<status>        `status` de `leads_funil`, uma das 11 chaves de `STATUS_LEAD`
 *                                 (`admin.html:3059`): novo, contatado, diagnostico_agendado,
 *                                 proposta_enviada, fechado, com_juridico, pagamento_a_receber,
 *                                 pagamento_recebido, em_consultoria, finalizada, perdido.
 *                                 Vem do clique numa barra ou etiqueta do funil.
 *   /clientes?etapa=<etapa>       `etapa` de `jornada_cliente`, uma das 5 chaves de `ORDEM_JORNADA`
 *                                 (`admin.html:3085`): diagnostico_inicial, questionario,
 *                                 avaliando_material, em_atendimento, concluido.
 *                                 Vem do clique numa barra ou etiqueta da jornada.
 *   /financeiro?de=AAAA-MM-DD&ate=AAAA-MM-DD
 *                                 O período do filtro de datas da aba Financeiro, que recorta as
 *                                 parcelas pelo VENCIMENTO (`admin.html:8172-8189`). Vem do cartão
 *                                 do Financeiro, com o primeiro e o último dia do mês corrente,
 *                                 para a aba abrir mostrando os mesmos números do cartão.
 *
 * As outras áreas (Compromissos, Matriz, Depoimentos, Arquivos, Sistema) abrem sem filtro.
 *
 * ESTADOS HONESTOS, POR CARTÃO: cada número é uma leitura independente. Carregando mostra o
 * esqueleto; zero é número e aparece como 0; leitura que falhou mostra traço no número e a frase
 * do que houve, sem derrubar os outros cartões; nada é estimado nem preenchido.
 */

// Estado, efeito, memória e callback do React.
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
// O dicionário: a frase em português é a chave.
import { t } from '@complexity/i18n';
// As peças do Chassi: cartão navegável, aviso, botão, gráfico interativo e os ícones por nome.
import { Aviso, Botao, Cartao, GraficoBarras, IconeChevron, icone, type BarraDado } from '@complexity/ui';
// O manifesto do admin: as áreas, com rota, rótulo, ícone e permissão, na ordem do menu.
import produtoAdmin from '../../../product.config.js';
// Sessão, papel e navegação, entregues pelo App.
import { usePainel } from '../../contexto.js';
// O que cada papel pode ver.
import { permissoesDoPapel } from '../../papeis.js';
// A fonte dos números e o vocabulário das etapas.
import {
  ErroDeLeitura,
  ETAPAS_DA_JORNADA,
  ETAPAS_DO_FUNIL,
  criarFonteRest,
  mesDoBanco,
  type ContagemPorEtapa,
  type Etapa,
  type FonteDoPainel,
} from './fonte.js';

/* ===========================================================================
   A LEITURA: três estados, e nenhum outro
   =========================================================================== */

// Carregando, pronta com o valor, ou falhou com a frase que a tela mostra.
type Leitura<T> =
  | { readonly estado: 'carregando' }
  | { readonly estado: 'pronta'; readonly valor: T }
  | { readonly estado: 'erro'; readonly frase: string };

// A frase de uma falha. Erro que não é `ErroDeLeitura` é defeito de código, e diz isso sem código técnico.
function fraseDoErro(erro: unknown): string {
  if (erro instanceof ErroDeLeitura) return erro.frase;
  // O detalhe vai para o console, onde quem mantém o sistema procura.
  console.error('Painel: falha inesperada numa leitura.', erro);
  return 'Não foi possível montar este número. O defeito é do sistema, e não do seu acesso.';
}

/**
 * Dispara uma leitura e acompanha o estado dela.
 *
 * `ligada` falso quer dizer que o papel não vê aquela área: a leitura nem sai, porque o RLS a
 * recusaria e o Painel não tem por que perguntar ao banco o que não vai mostrar.
 */
function useLeitura<T>(ligada: boolean, ler: () => Promise<T>): Leitura<T> {
  const [leitura, setLeitura] = useState<Leitura<T>>({ estado: 'carregando' });
  useEffect(() => {
    if (!ligada) return;
    // Protege contra gravar o resultado depois de a tela sair (a pessoa navegou antes de terminar).
    let viva = true;
    setLeitura({ estado: 'carregando' });
    ler().then(
      (valor) => {
        if (viva) setLeitura({ estado: 'pronta', valor });
      },
      (erro: unknown) => {
        if (viva) setLeitura({ estado: 'erro', frase: fraseDoErro(erro) });
      },
    );
    return () => {
      viva = false;
    };
  }, [ligada, ler]);
  return leitura;
}

// Tira um número de uma leitura maior (a jornada inteira vira "quantas em atendimento"), mantendo o estado.
function derivar<T>(leitura: Leitura<T>, extrair: (valor: T) => number): Leitura<number> {
  return leitura.estado === 'pronta' ? { estado: 'pronta', valor: extrair(leitura.valor) } : leitura;
}

/* ===========================================================================
   A ESCRITA DOS NÚMEROS, em português do Brasil
   =========================================================================== */

// Contagem com separador de milhar: 1.234.
const CONTAGEM = new Intl.NumberFormat('pt-BR');
// Dinheiro em reais, com centavos: R$ 1.234,56, como `formatarMoeda` do admin atual.
const MOEDA = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
// A data por extenso do alto da tela: sábado, 19 de setembro de 2026.
const DATA_POR_EXTENSO = new Intl.DateTimeFormat('pt-BR', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});
// O mês do cartão do Financeiro: setembro de 2026.
const MES_POR_EXTENSO = new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric' });

/* ===========================================================================
   O CARTÃO DE UMA ÁREA
   =========================================================================== */

// Um número do cartão: o rótulo, a leitura, e se é contagem ou dinheiro.
interface Numero {
  readonly id: string;
  readonly rotulo: string;
  readonly leitura: Leitura<number>;
  readonly moeda?: boolean;
}

// O valor escrito como a pessoa lê: número, dinheiro, ou traço quando a leitura falhou.
function textoDoNumero(numero: Numero): string {
  if (numero.leitura.estado === 'pronta') {
    return numero.moeda ? MOEDA.format(numero.leitura.valor) : CONTAGEM.format(numero.leitura.valor);
  }
  // Falhou: traço, que diz "não sei" sem inventar zero.
  if (numero.leitura.estado === 'erro') return '-';
  return t('carregando');
}

interface PropsCartaoDaArea {
  readonly area: string;
  readonly nome: string;
  readonly nomeDoIcone: string;
  readonly destino: string;
  readonly aoNavegar: (destino: string) => void;
  readonly numeros: readonly Numero[];
  // Uma linha de contexto sob o nome, quando os números precisam dela (o mês do Financeiro).
  readonly contexto?: string;
}

function CartaoDaArea({ area, nome, nomeDoIcone, destino, aoNavegar, numeros, contexto }: PropsCartaoDaArea) {
  // O desenho do ícone, pelo nome que o manifesto dá ao item de menu.
  const Icone = icone(nomeDoIcone);
  // As frases de falha, sem repetir: dois números da mesma leitura falham com a mesma frase.
  const frases = [...new Set(numeros.flatMap((n) => (n.leitura.estado === 'erro' ? [n.leitura.frase] : [])))];
  const carregando = numeros.some((n) => n.leitura.estado === 'carregando');
  // O que o leitor de tela anuncia: a área, cada número com o rótulo, a falha se houver, e o destino.
  const nomeAcessivel =
    `${t(nome)}: ` +
    numeros.map((n) => `${t(n.rotulo)} ${textoDoNumero(n)}`).join(', ') +
    '. ' +
    frases.map((f) => `${t(f)} `).join('') +
    t('Abrir a área.');

  return (
    // O `div` só carrega a marca que a suíte usa para achar o cartão da área, e `aria-busy`
    // avisa o leitor de tela de que os números ainda estão chegando.
    <div data-testid={`cartao-${area}`} aria-busy={carregando || undefined} className="min-w-0">
      <Cartao destino={destino} aoNavegar={aoNavegar} nomeAcessivel={nomeAcessivel}>
        <div className="flex items-center gap-cx-3">
          {/* O ícone da área numa pastilha da marca: a mesma figura do menu, para o olho ligar os dois. */}
          <span
            aria-hidden="true"
            className="flex h-cx-10 w-cx-10 shrink-0 items-center justify-center rounded-cx-cartao-interno bg-marca-suave text-marca"
          >
            {Icone ? <Icone className="h-cx-5 w-cx-5" /> : null}
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="text-cx-base font-semibold text-texto">{t(nome)}</h2>
            {contexto ? <p className="text-cx-xs text-texto-suave">{contexto}</p> : null}
          </div>
          {/* A seta diz "leva a outro lugar". O chevron do conjunto aponta para baixo, e gira para a direita. */}
          <IconeChevron aria-hidden="true" className="h-cx-5 w-cx-5 shrink-0 -rotate-90 text-texto-fraco" />
        </div>

        {/* Os números em linha que QUEBRA: em 390px o dinheiro não cabe em três colunas, e
            quebrar é melhor que cortar ou empurrar a página para o lado. */}
        <dl className="mt-cx-4 flex flex-wrap gap-x-cx-6 gap-y-cx-3">
          {numeros.map((n) => (
            <div key={n.id} className="min-w-0">
              <dt className="text-cx-xs text-texto-suave">{t(n.rotulo)}</dt>
              <dd
                data-testid={`numero-${area}-${n.id}`}
                className={`font-semibold tabular-nums text-texto ${n.moeda ? 'text-cx-xl' : 'text-cx-2xl'}`}
              >
                {n.leitura.estado === 'carregando' ? (
                  <>
                    {/* O esqueleto ocupa o lugar do número, para a tela não pular quando ele chega. */}
                    <span
                      aria-hidden="true"
                      className="inline-block h-cx-8 w-cx-12 animate-pulse rounded-cx-sm bg-superficie-afundada align-middle motion-reduce:animate-none"
                    />
                    <span className="sr-only">{t('carregando')}</span>
                  </>
                ) : (
                  textoDoNumero(n)
                )}
              </dd>
            </div>
          ))}
        </dl>

        {/* A falha dita em frase, dentro do cartão dela e de nenhum outro. */}
        {frases.length > 0 ? (
          <div className="mt-cx-4 flex flex-col gap-cx-2">
            {frases.map((f) => (
              <Aviso key={f} tom="erro">
                {t(f)}
              </Aviso>
            ))}
          </div>
        ) : null}
      </Cartao>
    </div>
  );
}

/* ===========================================================================
   O GRÁFICO DE ETAPAS: barras que levam à área recortada
   =========================================================================== */

interface PropsGraficoDeEtapas {
  readonly id: string;
  readonly titulo: string;
  readonly explicacao: string;
  readonly etapas: readonly Etapa[];
  readonly leitura: Leitura<ContagemPorEtapa>;
  readonly vazio: string;
  // A série (grupo da legenda) de cada etapa. A legenda liga e desliga cada grupo.
  readonly serieDe: (chave: string) => string;
  // O nome da área para onde a barra leva, para o botão e o leitor de tela dizerem o destino.
  readonly area: string;
  // Abre a área recortada na etapa.
  readonly aoAbrir: (chave: string) => void;
}

function GraficoDeEtapas({ id, titulo, explicacao, etapas, leitura, vazio, serieDe, area, aoAbrir }: PropsGraficoDeEtapas) {
  // Os grupos desligados pela legenda. Vive aqui porque a peça recebe o estado de fora.
  const [ocultas, setOcultas] = useState<ReadonlySet<string>>(new Set());

  // Liga ou desliga um grupo da legenda.
  const alternar = useCallback((serie: string) => {
    setOcultas((atual) => {
      const nova = new Set(atual);
      if (nova.has(serie)) nova.delete(serie);
      else nova.add(serie);
      return nova;
    });
  }, []);

  // Carregando: o título e um esqueleto do tamanho do gráfico, sem barra nenhuma inventada.
  if (leitura.estado === 'carregando') {
    return (
      <div data-testid={`grafico-${id}`} aria-busy="true">
        <Cartao titulo={titulo}>
          <div
            aria-hidden="true"
            className="h-cx-16 w-full animate-pulse rounded-cx-cartao-interno bg-superficie-afundada motion-reduce:animate-none"
          />
          <p className="sr-only">{t('carregando')}</p>
        </Cartao>
      </div>
    );
  }

  // Falhou: o título e a frase. O resto do Painel continua de pé.
  if (leitura.estado === 'erro') {
    return (
      <div data-testid={`grafico-${id}`}>
        <Cartao titulo={titulo}>
          <Aviso tom="erro">{t(leitura.frase)}</Aviso>
        </Cartao>
      </div>
    );
  }

  // Uma barra por etapa, na ordem do admin atual. Etapa com zero continua no eixo, rente ao chão.
  const dados: BarraDado[] = etapas.map((e) => ({
    chave: e.chave,
    rotulo: t(e.rotulo),
    valor: leitura.valor[e.chave] ?? 0,
    serie: serieDe(e.chave),
  }));
  const total = dados.reduce((soma, d) => soma + d.valor, 0);
  // O nome da etapa, para o botão de cada uma.
  const rotuloDe = (chave: string) => dados.find((d) => d.chave === chave)?.rotulo ?? chave;

  return (
    <div data-testid={`grafico-${id}`} className="flex min-w-0 flex-col gap-cx-3">
      {/*
        A LARGURA MÍNIMA É O ALVO DE TOQUE: cada coluna do gráfico é o alvo do clique, e com
        onze etapas em 390px ela teria 26px. O mínimo abaixo garante 44px por coluna (o token
        `alvo-toque`), mais os vãos e o respiro do cartão, todos tokens. Quando não cabe, o
        gráfico rola DENTRO da própria caixa, e a página continua sem rolagem lateral.
        O `espaco-2` final é a folga da borda de 1px de cada lado da `.cx-cartao`
        (`packages/ui/src/styles.css:133`): sem ela a coluna fecharia em 43,8px.
      */}
      <div className="overflow-x-auto">
        <div
          style={{
            minWidth: `calc(${dados.length} * var(--cx-alvo-toque) + ${Math.max(dados.length - 1, 0)} * var(--cx-espaco-1) + 2 * var(--cx-espaco-4) + var(--cx-espaco-2))`,
          }}
        >
          <GraficoBarras
            titulo={t(titulo)}
            // Total zero é ausência de dado, e não falha: a peça diz o motivo no lugar das barras.
            dados={total === 0 ? [] : dados}
            vazio={t(vazio)}
            aoSelecionar={aoAbrir}
            seriesOcultas={ocultas}
            aoAlternarSerie={alternar}
            aoLimpar={() => setOcultas(new Set())}
            rotuloLimpar={t('mostrar todas as etapas')}
            formatarValor={(v) => CONTAGEM.format(v)}
          />
        </div>
      </div>

      <p className="text-cx-xs text-texto-suave">{t(explicacao)}</p>

      {/*
        AS ETIQUETAS POR ETAPA, com o número escrito. Elas existem por causa do telefone: lá não
        há ponteiro para o tooltip, e o toque na barra já navega. Aqui o valor fica sempre à vista,
        e cada etiqueta é um segundo caminho para o mesmo destino, com alvo de 44px.
      */}
      {total > 0 ? (
        <ul className="flex flex-wrap gap-cx-2" aria-label={t(`${titulo}: abrir por etapa`)}>
          {dados.map((d) => (
            <li key={d.chave}>
              <Botao
                variante="secundario"
                tamanho="sm"
                onClick={() => aoAbrir(d.chave)}
                aria-label={`${t('Abrir')} ${t(area)} ${t('na etapa')} ${rotuloDe(d.chave)}: ${CONTAGEM.format(d.valor)}`}
              >
                <span>{d.rotulo}</span>
                <span className="font-semibold tabular-nums text-texto">{CONTAGEM.format(d.valor)}</span>
              </Botao>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/* ===========================================================================
   A TELA
   =========================================================================== */

export function TelaPainel() {
  const { sessao, acesso, navegar } = usePainel();
  // A fonte é criada uma vez por token: trocar de sessão troca a fonte, e nada mais a recria.
  const fonte: FonteDoPainel = useMemo(() => criarFonteRest(sessao.token), [sessao.token]);
  // O instante de referência da tela inteira. Fixo na montagem, para "hoje" não mudar no meio da leitura.
  const [agora] = useState(() => new Date());
  return <ConteudoDoPainel fonte={fonte} agora={agora} papel={acesso.papel} navegar={navegar} />;
}

interface PropsConteudo {
  readonly fonte: FonteDoPainel;
  readonly agora: Date;
  readonly papel: string;
  readonly navegar: (rota: string) => void;
}

/**
 * O conteúdo, separado da tela para receber a fonte por propriedade: quando a camada
 * `@studiolu/dados` chegar, é só a `TelaPainel` que muda de fonte.
 */
function ConteudoDoPainel({ fonte, agora, papel, navegar }: PropsConteudo) {
  // As permissões do papel, que decidem cartões, gráficos e leituras.
  const permissoes = permissoesDoPapel(papel);
  const ve = (permissao: string) => permissoes.includes(permissao);

  // As leituras, uma por conjunto de números. Cada `ler` é estável, para o efeito não repetir.
  const compromissos = useLeitura(ve('compromissos:ver'), useCallback(() => fonte.compromissos(agora), [fonte, agora]));
  const clientes = useLeitura(ve('clientes:ver'), useCallback(() => fonte.clientes(), [fonte]));
  const jornada = useLeitura(ve('clientes:ver'), useCallback(() => fonte.jornada(), [fonte]));
  const funil = useLeitura(ve('vendas:ver'), useCallback(() => fonte.funil(), [fonte]));
  const matriz = useLeitura(ve('matriz:ver'), useCallback(() => fonte.matriz(), [fonte]));
  const depoimentos = useLeitura(ve('depoimentos:ver'), useCallback(() => fonte.depoimentos(), [fonte]));
  const financeiro = useLeitura(ve('financeiro:ver'), useCallback(() => fonte.financeiro(agora), [fonte, agora]));
  const arquivos = useLeitura(ve('arquivos:ver'), useCallback(() => fonte.arquivos(), [fonte]));
  const usuarios = useLeitura(ve('sistema:ver'), useCallback(() => fonte.usuarios(), [fonte]));

  // O mês corrente, para o destino do Financeiro e para a linha de contexto do cartão.
  const mes = mesDoBanco(agora);
  const nomeDoMes = MES_POR_EXTENSO.format(agora);

  // Soma todas as etapas de uma contagem.
  const somaDe = (c: ContagemPorEtapa) => Object.values(c).reduce((s, n) => s + n, 0);

  // Os números e o destino de cada área, pela rota do manifesto.
  const porArea: Readonly<Record<string, { numeros: readonly Numero[]; destino?: string; contexto?: string }>> = {
    '/compromissos': {
      numeros: [
        { id: 'hoje', rotulo: 'Hoje', leitura: derivar(compromissos, (c) => c.hoje) },
        { id: 'semana', rotulo: 'Nesta semana', leitura: derivar(compromissos, (c) => c.semana) },
      ],
    },
    '/clientes': {
      numeros: [
        { id: 'total', rotulo: 'Cadastradas', leitura: clientes },
        { id: 'em-atendimento', rotulo: 'Em atendimento', leitura: derivar(jornada, (j) => j['em_atendimento'] ?? 0) },
        // "Concluídas", e não "Jornada concluída": com o rótulo longo o terceiro número quebrava de
        // linha no cartão de 1280 (captura de 19/09/2026), e os três cabem numa linha só assim.
        { id: 'concluidas', rotulo: 'Concluídas', leitura: derivar(jornada, (j) => j['concluido'] ?? 0) },
      ],
    },
    '/vendas': {
      numeros: [
        { id: 'total', rotulo: 'Leads', leitura: derivar(funil, somaDe) },
        { id: 'novos', rotulo: 'Novos', leitura: derivar(funil, (f) => f['novo'] ?? 0) },
        { id: 'propostas', rotulo: 'Propostas enviadas', leitura: derivar(funil, (f) => f['proposta_enviada'] ?? 0) },
      ],
    },
    '/matriz': {
      numeros: [
        { id: 'em-andamento', rotulo: 'Em andamento', leitura: derivar(matriz, (m) => m.emAndamento) },
        { id: 'finalizados', rotulo: 'Finalizados', leitura: derivar(matriz, (m) => m.finalizados) },
        { id: 'lacunas', rotulo: 'Lacunas', leitura: derivar(matriz, (m) => m.lacunas) },
      ],
    },
    '/depoimentos': {
      numeros: [
        { id: 'autorizados', rotulo: 'Autorizados', leitura: derivar(depoimentos, (d) => d.autorizados) },
        { id: 'aprovados', rotulo: 'Aprovados para uso', leitura: derivar(depoimentos, (d) => d.aprovados) },
      ],
    },
    '/financeiro': {
      // A aba abre recortada no mesmo mês, para mostrar os mesmos números do cartão.
      destino: `/financeiro?de=${mes.de}&ate=${mes.ate}`,
      contexto: `${t('Parcelas com vencimento em')} ${nomeDoMes}`,
      numeros: [
        { id: 'recebido', rotulo: 'Recebido', leitura: derivar(financeiro, (f) => f.recebido), moeda: true },
        { id: 'a-receber', rotulo: 'A receber', leitura: derivar(financeiro, (f) => f.aReceber), moeda: true },
      ],
    },
    '/arquivos': {
      numeros: [{ id: 'total', rotulo: 'Arquivos', leitura: arquivos }],
    },
    '/sistema': {
      numeros: [{ id: 'usuarios', rotulo: 'Usuários do painel', leitura: usuarios }],
    },
  };

  // As áreas do menu que o papel vê, na ordem do menu, tirando o próprio Painel.
  const areas = produtoAdmin.menu.filter(
    (item) => item.rota !== '/painel' && (item.permissoes ?? []).every((p) => ve(p)),
  );

  // Abre Vendas recortada numa etapa do funil, e Clientes recortada numa etapa da jornada.
  const abrirVendas = useCallback((etapa: string) => navegar(`/vendas?etapa=${encodeURIComponent(etapa)}`), [navegar]);
  const abrirClientes = useCallback((etapa: string) => navegar(`/clientes?etapa=${encodeURIComponent(etapa)}`), [navegar]);

  const veFunil = ve('vendas:ver');
  const veJornada = ve('clientes:ver');

  // Os gráficos que o papel vê, cada um no seu bloco.
  const graficos: ReactNode[] = [];
  if (veFunil) {
    graficos.push(
      // Com a jornada ao lado, o funil ocupa dois terços: onze etapas pedem mais largura que cinco.
      <div key="funil" className={veJornada ? 'min-w-0 lg:col-span-2' : 'min-w-0 lg:col-span-3'}>
        <GraficoDeEtapas
          id="funil"
          titulo="Funil de leads por etapa"
          explicacao="Cada barra é uma coluna do Kanban de Vendas. Toque numa barra ou numa etapa para abrir Vendas já filtrada."
          etapas={ETAPAS_DO_FUNIL}
          leitura={funil}
          vazio="Nenhum lead cadastrado ainda."
          // A legenda separa os perdidos, que o admin atual já trata à parte (`admin.html:3587`).
          serieDe={(chave) => (chave === 'perdido' ? 'Perdidos' : 'Demais etapas')}
          area="Vendas"
          aoAbrir={abrirVendas}
        />
      </div>,
    );
  }
  if (veJornada) {
    graficos.push(
      <div key="jornada" className={veFunil ? 'min-w-0 lg:col-span-1' : 'min-w-0 lg:col-span-3'}>
        <GraficoDeEtapas
          id="jornada"
          titulo="Jornada das clientes por etapa"
          explicacao="Conta as jornadas registradas, uma por cliente e serviço. A cliente sem jornada registrada aparece na ficha como Diagnóstico Inicial e não entra nesta conta."
          etapas={ETAPAS_DA_JORNADA}
          leitura={jornada}
          vazio="Nenhuma jornada registrada ainda."
          // Os papéis `info` e `sucesso` a peça já escreve como "Em andamento" e "Concluído".
          serieDe={(chave) => (chave === 'concluido' ? 'sucesso' : 'info')}
          area="Clientes"
          aoAbrir={abrirClientes}
        />
      </div>,
    );
  }

  return (
    <div data-testid="painel" className="flex min-w-0 flex-col gap-cx-6">
      {/* A data de referência dos números: "hoje" e "nesta semana" dependem dela. */}
      <p className="text-cx-sm text-texto-suave">
        {t('Números de')} {DATA_POR_EXTENSO.format(agora)}. {t('Cada cartão abre a área.')}
      </p>

      {/* Um cartão por área. Uma coluna no telefone, duas no tablet, três no computador. */}
      <div className="grid grid-cols-1 gap-cx-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
        {areas.map((item) => {
          const dados = porArea[item.rota];
          // Área nova no manifesto sem números definidos aqui: não entra, em vez de entrar vazia.
          if (!dados) return null;
          return (
            <CartaoDaArea
              key={item.rota}
              area={item.rota.slice(1)}
              nome={item.label}
              nomeDoIcone={item.icone}
              destino={dados.destino ?? item.rota}
              aoNavegar={navegar}
              numeros={dados.numeros}
              {...(dados.contexto ? { contexto: dados.contexto } : {})}
            />
          );
        })}
      </div>

      {/* Os gráficos, abaixo dos cartões: o cartão responde "quanto", o gráfico responde "onde". */}
      {graficos.length > 0 ? <div className="grid grid-cols-1 gap-cx-4 lg:grid-cols-3">{graficos}</div> : null}
    </div>
  );
}
